import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Play, Pause, Volume2, VolumeX, Maximize, Minimize, Loader2 } from 'lucide-react';

/**
 * YouTubePlayer
 * ------------------------------------------------------------------
 * Wraps a YouTube iframe and hides YouTube's chrome (title bar,
 * "YouTube" logo, channel branding, "More videos" endscreen, etc.)
 * with masking overlays and provides custom Mux-styled controls.
 *
 * The iframe has pointer-events disabled so no clicks can reach the
 * YouTube UI. All playback is driven through the YouTube IFrame API.
 */

let ytApiPromise = null;
const loadYouTubeAPI = () => {
  if (ytApiPromise) return ytApiPromise;
  ytApiPromise = new Promise((resolve) => {
    if (window.YT && window.YT.Player) {
      resolve(window.YT);
      return;
    }
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof prev === 'function') prev();
      resolve(window.YT);
    };
    if (!document.querySelector('script[data-yt-api="1"]')) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      tag.setAttribute('data-yt-api', '1');
      document.head.appendChild(tag);
    }
  });
  return ytApiPromise;
};

const formatTime = (secs) => {
  if (!Number.isFinite(secs) || secs < 0) return '0:00';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = Math.floor(secs % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const YouTubePlayer = ({
  videoId,
  autoPlay = true,
  poster = null,
  className = '',
  onEnded = null,
  onStart = null,
  startMuted = false,
}) => {
  const containerRef = useRef(null);
  const iframeHostRef = useRef(null);
  const playerRef = useRef(null);
  const hideTimerRef = useRef(null);
  const rafRef = useRef(null);

  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(startMuted);
  const [volume, setVolume] = useState(startMuted ? 0 : 80);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);

  // Poll current time
  const tick = useCallback(() => {
    const p = playerRef.current;
    if (p && typeof p.getCurrentTime === 'function') {
      try {
        setCurrentTime(p.getCurrentTime() || 0);
        if (typeof p.getVideoLoadedFraction === 'function') {
          setBuffered(p.getVideoLoadedFraction() || 0);
        }
      } catch (_e) { /* noop */ }
    }
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    let cancelled = false;
    loadYouTubeAPI().then((YT) => {
      if (cancelled || !iframeHostRef.current) return;
      playerRef.current = new YT.Player(iframeHostRef.current, {
        videoId,
        playerVars: {
          autoplay: autoPlay ? 1 : 0,
          controls: 0,
          modestbranding: 1,
          rel: 0,
          showinfo: 0,
          iv_load_policy: 3,
          fs: 0,
          disablekb: 1,
          playsinline: 1,
          cc_load_policy: 0,
          origin: window.location.origin,
        },
        events: {
          onReady: (e) => {
            setReady(true);
            setDuration(e.target.getDuration() || 0);
            try {
              if (startMuted) e.target.mute();
              else e.target.setVolume(volume);
              if (autoPlay) e.target.playVideo();
            } catch (_er) { /* noop */ }
            rafRef.current = requestAnimationFrame(tick);
          },
          onStateChange: (e) => {
            const YTState = window.YT.PlayerState;
            if (e.data === YTState.PLAYING) {
              setPlaying(true);
              if (!hasStarted) {
                setHasStarted(true);
                if (typeof onStart === 'function') onStart();
              }
            } else if (e.data === YTState.PAUSED) {
              setPlaying(false);
            } else if (e.data === YTState.ENDED) {
              setPlaying(false);
              if (typeof onEnded === 'function') onEnded();
            } else if (e.data === YTState.BUFFERING) {
              // keep current playing flag
            }
          },
        },
      });
    });
    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      try { playerRef.current && playerRef.current.destroy && playerRef.current.destroy(); } catch (_e) { /* noop */ }
      playerRef.current = null;
    };
  }, [videoId]);

  // Auto-hide controls after inactivity
  const revealControls = useCallback(() => {
    setShowControls(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      if (playerRef.current && typeof playerRef.current.getPlayerState === 'function') {
        try {
          const s = playerRef.current.getPlayerState();
          if (s === window.YT.PlayerState.PLAYING) setShowControls(false);
        } catch (_e) { /* noop */ }
      }
    }, 2800);
  }, []);

  useEffect(() => {
    revealControls();
  }, [playing, revealControls]);

  const togglePlay = () => {
    const p = playerRef.current; if (!p) return;
    try {
      if (playing) p.pauseVideo(); else p.playVideo();
    } catch (_e) { /* noop */ }
    revealControls();
  };

  const toggleMute = () => {
    const p = playerRef.current; if (!p) return;
    try {
      if (muted) { p.unMute(); p.setVolume(volume || 80); setMuted(false); if (!volume) setVolume(80); }
      else { p.mute(); setMuted(true); }
    } catch (_e) { /* noop */ }
    revealControls();
  };

  const handleVolume = (e) => {
    const v = Number(e.target.value);
    const p = playerRef.current; if (!p) return;
    try {
      p.setVolume(v);
      if (v === 0) { p.mute(); setMuted(true); }
      else { if (muted) p.unMute(); setMuted(false); }
    } catch (_e) { /* noop */ }
    setVolume(v);
  };

  const handleSeek = (e) => {
    const p = playerRef.current; if (!p) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    try { p.seekTo(pct * duration, true); } catch (_e) { /* noop */ }
    setCurrentTime(pct * duration);
    revealControls();
  };

  const toggleFullscreen = () => {
    const el = containerRef.current; if (!el) return;
    if (!document.fullscreenElement) {
      (el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen).call(el);
    } else {
      (document.exitFullscreen || document.webkitExitFullscreen || document.msExitFullscreen).call(document);
    }
  };

  useEffect(() => {
    const onFs = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  const progressPct = duration > 0 ? (currentTime / duration) * 100 : 0;
  const bufferedPct = Math.min(100, buffered * 100);

  return (
    <div
      ref={containerRef}
      className={`relative bg-black w-full h-full overflow-hidden select-none ${className}`}
      onMouseMove={revealControls}
      onMouseLeave={() => playing && setShowControls(false)}
      onClick={togglePlay}
      data-testid="youtube-player"
    >
      {/* IFrame host */}
      <div className="absolute inset-0 w-full h-full">
        {/* Scale up slightly so YouTube's edge branding is cropped */}
        <div
          ref={iframeHostRef}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          style={{
            width: 'calc(100% + 120px)',
            height: 'calc(100% + 140px)',
            pointerEvents: 'none',
          }}
        />
      </div>

      {/* Loading / poster overlay before iframe ready */}
      {!ready && (
        <div className="absolute inset-0 bg-black flex items-center justify-center z-30">
          {poster ? (
            <img src={poster} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70" />
          ) : null}
          <Loader2 className="h-12 w-12 text-electric animate-spin relative z-10" />
        </div>
      )}

      {/* Top mask — hides YouTube's title/channel bar */}
      <div
        className="absolute top-0 left-0 right-0 h-20 pointer-events-none z-10"
        style={{
          background: 'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.85) 60%, rgba(0,0,0,0) 100%)',
        }}
      />

      {/* Bottom-right small mask — hides YouTube logo when it appears */}
      <div className="absolute bottom-0 right-0 w-32 h-16 pointer-events-none z-10 bg-black" />

      {/* Big center Play button when paused and started */}
      {ready && !playing && hasStarted && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); togglePlay(); }}
          className="absolute inset-0 flex items-center justify-center z-20"
          data-testid="youtube-center-play"
        >
          <span className="w-20 h-20 rounded-full bg-white/10 backdrop-blur-md border border-white/30 flex items-center justify-center hover:bg-white/20 transition">
            <Play className="h-10 w-10 text-white ml-1" fill="white" />
          </span>
        </button>
      )}

      {/* Custom control bar */}
      <div
        className={`absolute left-0 right-0 bottom-0 z-20 px-4 pb-4 pt-10 transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.4) 60%, rgba(0,0,0,0) 100%)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Seek bar */}
        <div
          className="w-full h-1.5 bg-white/20 rounded-full cursor-pointer relative mb-3 group"
          onClick={handleSeek}
          data-testid="youtube-seek-bar"
        >
          <div className="absolute inset-y-0 left-0 bg-white/30 rounded-full" style={{ width: `${bufferedPct}%` }} />
          <div className="absolute inset-y-0 left-0 bg-electric rounded-full" style={{ width: `${progressPct}%` }} />
          <div
            className="absolute top-1/2 -translate-y-1/2 h-3 w-3 rounded-full bg-electric opacity-0 group-hover:opacity-100 transition-opacity"
            style={{ left: `calc(${progressPct}% - 6px)` }}
          />
        </div>

        {/* Controls row */}
        <div className="flex items-center justify-between text-white">
          <div className="flex items-center space-x-4">
            <button type="button" onClick={togglePlay} className="p-1 hover:text-electric transition" data-testid="youtube-play-btn">
              {playing ? <Pause className="h-6 w-6" fill="currentColor" /> : <Play className="h-6 w-6" fill="currentColor" />}
            </button>
            <div className="flex items-center space-x-2 group">
              <button type="button" onClick={toggleMute} className="p-1 hover:text-electric transition" data-testid="youtube-mute-btn">
                {muted || volume === 0 ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
              </button>
              <input
                type="range"
                min="0"
                max="100"
                value={muted ? 0 : volume}
                onChange={handleVolume}
                className="w-0 group-hover:w-20 transition-all duration-200 accent-electric cursor-pointer"
                data-testid="youtube-volume"
              />
            </div>
            <span className="text-xs tabular-nums text-white/80">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <button type="button" onClick={toggleFullscreen} className="p-1 hover:text-electric transition" data-testid="youtube-fs-btn">
              {isFullscreen ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default YouTubePlayer;
